import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { isNil, isEmpty, forEach, get } from 'lodash';

import Axios from 'src/services/axios';
import container from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePopoverSensitive } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import {
  PricingGrid,
  PricingGridProps,
  ComponentTypeGridDispatchProps,
} from 'src/pages/AssortmentBuild/Pricing/PricingGrid';
import {
  fetchOverTimeData,
  receivePricingEvents,
  receivePricingOverTimeConfig,
  submitPayload as submitPricingPayload,
  cleanUp as cleanupPricing,
  receiveSomePricingOverTimeConfig,
  fetchMassEditData,
} from 'src/pages/AssortmentBuild/Pricing/PricingOverTime.slice';
import { PricingEventResponse } from 'src/pages/AssortmentBuild/Pricing/PricingOverTime.types';
import { convertPivotItemsToRowData } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheet.utils';
import { RowData } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheet.types';
import { ASSORTMENT, ASSORTMENT_BUILD_FILTER_WARNING } from 'src/utils/Domain/Constants';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { TreePivot } from 'src/worker/pivotWorker.types';

import Subheader from 'src/components/Subheader/Subheader.container';
import { LegacySubheaderActionButtonProps } from 'src/components/Subheader/Subheader.types';
import { getUniqueDataFromCache, HashType } from 'src/services/pivotServiceCache';
import { MassEdit, MassEditConfig, MassEditSubmissionData } from 'src/components/MassEdit/MassEdit';
import { getLocalConfig } from 'src/components/ViewConfiguratorModal/ViewConfiguratorModal.utils';
import { FavoriteResponseItem } from 'src/components/Subheader/Favorites/Favorites.types';
import { TabbedComponentWrapperOwnProps } from 'src/pages/Worklist/Worklist.types';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import { AdornmentType } from 'configuration-schemas/viewdefns/literals';
import {
  selectTabbedPricingDataLoaded,
  selectTabbedPricingHasMassEditViewDefn,
  selectPricingHasMassEditModel,
  selectTabbedPricingMassEditData,
  selectTabbedPricingViewDataStates,
  selectTabbedPricingMassEditDataLoaded,
} from './PricingOverTime.selectors';
import { ViewDataState } from 'src/types/Domain';

type PricingProps = ReturnType<typeof mapStateToPropsPricing>;
type PricingGridWithSubheader = PricingGridProps & {
  subheaderSummary?: string;
  overtimeData?: TreePivot;
  subheaderErrorText?: string;
} & PricingProps;

export function mapStateToPropsPricing(state: AppState, ownProps: TabbedComponentWrapperOwnProps) {
  const { pages, scope, subheader } = state;
  const viewState = pages.assortmentBuild.pricing;
  const scopeStart = !isNil(scope.scope.start) ? scope.scope.start : undefined;
  const { viewDefns, viewDataStateOverTime } = viewState;
  const rangeList = scope.rangeList;
  const allowFrom = !isEmpty(rangeList) ? rangeList[0].id : '';
  const allowTo = !isEmpty(rangeList) ? rangeList[rangeList.length - 1].id : '';
  const isMassEditDataLoaded = selectTabbedPricingMassEditDataLoaded(state, ownProps.massEditModel, ownProps.viewDefns);
  const massEditData = selectTabbedPricingMassEditData(state, ownProps.massEditModel);
  const viewDataStates = selectTabbedPricingViewDataStates(state, ownProps.massEditModel);

  const loaded = selectTabbedPricingDataLoaded(state, ownProps.massEditModel);
  const overtimeData = getUniqueDataFromCache(viewState, HashType.pricingOverTime)?.tree || [];
  const isLiveDataReady = viewDataStateOverTime === ViewDataState.liveDataReady;
  const groupRowHeight = viewState.viewDefns.grid.main?.groupRowHeight;
  const adornments: AdornmentType[] = get(viewState.viewDefns, 'grid.adornments', []);

  return {
    loaded,
    timeEntries: scope.timeInfo.entries,
    rowData: [],
    overtimeData,
    anchorField: scopeStart,
    rowHeight: viewDefns?.grid.main?.rowHeight,
    groupRowHeight,
    editable: isLiveDataReady,
    itemId: ownProps.selectedItemId || '',
    ...subheader,
    ...viewState,
    allowFrom,
    allowTo,
    rangeList: scope.daysRangeList,
    isMassEditDataLoaded,
    massEditData,
    showFlowStatus: ownProps.showFlowStatus === false ? false : true,
    adornments,
    viewDataStates,
  };
}

export function dispatchToPropsPricing(
  dispatch: AppThunkDispatch,
  ownProps: TabbedComponentWrapperOwnProps,
): ComponentTypeGridDispatchProps {
  return {
    onRenderGrid: async () => {
      const { massEditModel, viewDefns } = ownProps;
      // for legacy reasons, the massEditModel will never be required
      const hasMassEditModel = selectPricingHasMassEditModel({} as AppState, massEditModel);
      // when only the massEdit viewDefn is detected, the worklist companion data will be used by default
      const hasMassEditViewDefnConfigured = selectTabbedPricingHasMassEditViewDefn({} as AppState, viewDefns);

      const configResponse = await container.tenantConfigClient.getTenantViewDefnsWithFavorites({
        defnIds: viewDefns,
        appName: ASSORTMENT,
      });

      const massEditConfig = configResponse[1];
      const massEdit = hasMassEditViewDefnConfigured ? (massEditConfig as unknown as MassEditConfig) : undefined;

      const favoritesList = configResponse[2];
      const unmodifiedViewDefn: TenantConfigViewData = configResponse[0];
      const localConfig = getLocalConfig(
        viewDefns[0],
        favoritesList as unknown as FavoriteResponseItem[],
        dispatch,
        unmodifiedViewDefn,
      );

      const configWithFavorites = isNil(localConfig) ? unmodifiedViewDefn : localConfig.config;

      dispatch(
        receivePricingOverTimeConfig({
          grid: configWithFavorites || unmodifiedViewDefn,
          listSort: {} as TenantConfigViewData,
          subheaderRollUp: {} as TenantConfigViewData,
          list: {} as TenantConfigViewData,
          massEdit,
          unmodifiedViewDefn,
        }),
      );
      const eventResponse = await Axios.get<PricingEventResponse>(`/api/v2/events?appName=${ASSORTMENT}`);
      dispatch(receivePricingEvents(eventResponse.data.data));
      // second condition is technically redundant but is here to satisfy typescript
      if (hasMassEditModel && massEditModel) {
        dispatch(fetchMassEditData(massEditModel));
      }
      dispatch(fetchOverTimeData(ownProps.selectedItemId));
    },
    onCompanionItemChange() {
      dispatch(fetchOverTimeData(ownProps.selectedItemId));
    },
    onUpdateConfig(config: TenantConfigViewData) {
      dispatch(receiveSomePricingOverTimeConfig(config));
    },
    onRefetchOvertimeData: async () => {
      return dispatch(fetchOverTimeData(ownProps.selectedItemId));
    },
    submitPayload: async (payload: GranularEditPayloadItem[], shouldRefresh: boolean) => {
      return dispatch(submitPricingPayload(payload, shouldRefresh));
    },
    onCleanup: () => {
      dispatch(cleanupPricing());
    },
  };
}

const PricingGridWithSubheader = ({ subheaderSummary, ...props }: PricingGridWithSubheader) => {
  const {
    allowFrom,
    allowTo,
    anchorField,
    rangeList,
    isMassEditDataLoaded,
    viewDefns,
    massEditData,
    showFlowStatus,
    overtimeData,
    showUndoBtn,
    viewDataStates,
  } = props;
  const { massEdit: massEditConfig } = viewDefns;
  const configViewItems = massEditConfig ? MassEdit.getConfigViewItems(massEditConfig) : [];
  const itemsDataIndex = !isEmpty(configViewItems) ? configViewItems[1].dataIndex : '';
  const editableItems =
    massEditData &&
    massEditData.map((dataItem) => {
      const label = `${dataItem['name']}-${dataItem[itemsDataIndex]}`;
      return {
        value: dataItem['id'],
        label,
      };
    });

  const [rowData, setRowData] = useState([] as RowData[]);
  useEffect(() => {
    if (overtimeData != null) {
      const requiredKeys = ['addoff', 'eo', 'eff_aur', 'corpaddoff', 'corpexcl', 'cccurp', 'ccdiscountpct'];
      setRowData(convertPivotItemsToRowData(overtimeData, viewDefns.grid.view, requiredKeys, 'stylecolor', 'week'));
    }
  }, [overtimeData, viewDefns]);

  const handleMassEditSubmission = async (submission: MassEditSubmissionData) => {
    const { selectedModifier, selectedItems, selectedWeekList, modifierValue } = submission;

    if (!isNil(selectedModifier) && !isNil(selectedWeekList)) {
      // no null submission data expected, except for modifier value
      const updatedData: GranularEditPayloadItem[] = [];

      forEach(selectedWeekList, (weekNumber) => {
        const updatedWeekData = selectedItems.map((item) => {
          return {
            [selectedModifier]: modifierValue,
            coordinates: {
              product: item.value, // styleColor id
              time: weekNumber,
            },
          };
        });

        updatedData.push(...updatedWeekData);
      });

      if (props.submitPayload) {
        await props.submitPayload(updatedData, true);
      }

      // update grid data
      if (props.onRefetchOvertimeData) {
        props.onRefetchOvertimeData();
      }
    }
  };

  const onUpdateConfig = (config: TenantConfigViewData) => {
    props.onUpdateConfig && props.onUpdateConfig(config);
  };

  const generateExtraActionButtonProps = (): LegacySubheaderActionButtonProps => {
    const massEdit = isNil(massEditConfig)
      ? undefined
      : {
          config: massEditConfig,
          allowFrom,
          allowTo,
          scopeStart: anchorField || '',
          rangeList,
          editableItems,
          title: 'Mass Copy Pricing Events',
          handleSubmit: handleMassEditSubmission,
          dataLoading: !isMassEditDataLoaded,
        };

    return {
      massEdit,
    };
  };

  const viewConfigurator = viewDefns &&
    viewDefns.unmodifiedViewDefn && {
      viewConfig: viewDefns.grid,
      unmodifiedViewDefn: viewDefns.unmodifiedViewDefn,
      showPinCheckboxForGrid: false,
      updateConfig: onUpdateConfig,
    };

  const extraActionButtons = generateExtraActionButtonProps();
  const fsGridProps = {
    ...props,
    rowData,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Subheader
        title={''}
        errorCondition={props.subheaderErrorText ? [props.subheaderErrorText] : [ASSORTMENT_BUILD_FILTER_WARNING]}
        // search is rendered in the companion view already
        showSearch={false}
        showFlowStatus={showFlowStatus}
        showUndoBtn={showUndoBtn}
        summary={subheaderSummary}
        viewConfigurator={viewConfigurator}
        extraLegacyActionButtons={extraActionButtons}
        viewDataState={viewDataStates}
      />
      <div style={{ flex: 1 }}>
        <PricingGrid {...fsGridProps} />
      </div>
    </div>
  );
};

export const TabbedPricingGrid = connect(
  mapStateToPropsPricing,
  dispatchToPropsPricing,
  // @ts-ignore
)(makeScopeAndFilterSensitive(makePopoverSensitive(PricingGridWithSubheader)));
